import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchApplicationFunction,
  updateApplicationFunction,
  fetchApplicationCategory,
  fetchApplicationTasks,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function ApplicationFunctionEditPage() {
  const { businessDomainId, categoryId, functionId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [original, setOriginal] = useState(null);
  const [applicationTasks, setApplicationTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState("");
  const [form, setForm] = useState({ applicationFunctionName: "", applicationFunctionDescription: "", url: "" });
  const [category, setCategory] = useState(null);

  const basePath = `/strategy/${businessDomainId}/application-categories/${categoryId}/application-functions`;

  const loadData = useCallback(async () => {
    try {
      const [fn, cat] = await Promise.all([
        fetchApplicationFunction(functionId),
        fetchApplicationCategory(categoryId),
      ]);
      setOriginal(fn);
      setForm({
        applicationFunctionName: fn.applicationFunctionName || "",
        applicationFunctionDescription: fn.applicationFunctionDescription || "",
        url: fn.url || "",
      });
      setSelectedTask(fn.applicationTaskId || "");
      setCategory(cat);

      // アプリケーションタスク一覧を取得
      const appTypes = cat.associatedApplicationTypes || [];
      let tasks = [];
      for (const at of appTypes) {
        const data = await fetchApplicationTasks(at.applicationTypeId);
        tasks = tasks.concat(Array.isArray(data) ? data : []);
      }
      setApplicationTasks(tasks);
    } catch (e) { notify.apiError(e); }
  }, [functionId, categoryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!original) return;
    if (!selectedTask) {
      notify.error(t("applicationCategory.applicationTask.selectRequired"));
      return;
    }
    const task = applicationTasks.find((t) => t.applicationTaskId === selectedTask);
    try {
      await updateApplicationFunction({
        ...original,
        companyId: COMPANY_ID,
        applicationCategoryId: categoryId,
        applicationCategoryName: category?.applicationCategoryName || original.applicationCategoryName || "",
        applicationTaskId: selectedTask,
        applicationTaskName: task?.applicationTaskName || original.applicationTaskName || "",
        applicationFunctionName: form.applicationFunctionName,
        applicationFunctionDescription: form.applicationFunctionDescription || null,
        url: form.url || null,
      });
      notify.success(t("message.update.success"));
      navigate(`${basePath}/${functionId}`);
    } catch (err) { notify.apiError(err); }
  };

  if (!original) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("applicationCategory.applicationFunction.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>
        <FormControl fullWidth required sx={{ mb: 3 }}>
          <InputLabel>{t("applicationCategory.applicationTaskName")}</InputLabel>
          <Select value={selectedTask} onChange={(e) => setSelectedTask(e.target.value)}
            label={t("applicationCategory.applicationTaskName")}>
            {applicationTasks.map((task) => (
              <MenuItem key={task.applicationTaskId} value={task.applicationTaskId}>
                {task.applicationTaskName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField fullWidth required sx={{ mb: 3 }}
          label={t("applicationCategory.applicationFunctionName")}
          name="applicationFunctionName"
          value={form.applicationFunctionName}
          onChange={handleChange}
        />

        <TextField fullWidth sx={{ mb: 3 }}
          label={t("common.url")}
          name="url"
          value={form.url}
          onChange={handleChange}
          placeholder="https://..."
        />

        <TextField fullWidth multiline rows={3} sx={{ mb: 3 }}
          label={t("common.description")}
          name="applicationFunctionDescription"
          value={form.applicationFunctionDescription}
          onChange={handleChange}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`${basePath}/${functionId}`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
